/**
 * PlatformApi provider — feeds a `PlatformApi` instance into the React
 * tree, configured with a token resolver that returns a Lexiun JWT.
 *
 * Updated 2026-04-30 auth-flow pivot: the dev-only browser-side autosign
 * (`getOrCreateDevToken`) is DELETED. Token resolution is now uniform:
 *   1. Reuse the cached Lexiun JWT from `lib/lexiun-jwt.ts` (which goes
 *      through Clerk-exchange OR a dev-login seed in localStorage).
 *   2. Return null if neither is available — PlatformApi then sends an
 *      unauthenticated request, lexserver returns 401, and the UI surfaces
 *      it as a permission error.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { PlatformApi } from "../lib/platform-api";
import { getLexiunToken } from "../lib/lexiun-jwt";

const PlatformApiContext = createContext<PlatformApi | null>(null);

const LEXSERVER_BASE = "/lexserver";

interface Props {
  children: ReactNode;
  /**
   * Pre-pivot LexPortal accepted a Clerk-token getter and exchanged it
   * inline. Post-pivot the exchange happens inside `getLexiunToken`
   * (driven by the module-level Clerk-token resolver registered by
   * `<ClerkTokenBridge>` in App.tsx). The prop is preserved so callers
   * don't break, but is no longer consulted.
   */
  getClerkToken?: () => Promise<string | null>;
}

export function PlatformApiProvider({ children }: Props) {
  const api = useMemo(
    () => new PlatformApi(LEXSERVER_BASE, () => getLexiunToken()),
    [],
  );

  return <PlatformApiContext value={api}>{children}</PlatformApiContext>;
}

export function usePlatformApi(): PlatformApi {
  const ctx = useContext(PlatformApiContext);
  if (!ctx) throw new Error("usePlatformApi must be used within PlatformApiProvider");
  return ctx;
}
